import cn from 'classnames'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { setCockpitShowQuota } from '../../model'
import { selectCockpitShowQuota } from '../../model/selectors'

import styles from './CockpitShowQuota.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.showQuota

/* The icon font has no money mark, so the coin is drawn here, in the chip's ink. */
const MoneyIcon = () => (
  <svg
    className={styles.icon}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="10" cy="10" r="7.5" />
    <path d="M12.3 7.6c-.4-.8-1.3-1.3-2.3-1.3-1.3 0-2.3.7-2.3 1.7 0 2.2 4.6 1.3 4.6 3.6 0 1-1 1.8-2.3 1.8-1.1 0-2-.5-2.4-1.4M10 5v10" />
  </svg>
)

interface ICockpitShowQuotaProps {
  /** In a tile header the control is a chip like the panel's other controls, not a switch row.
   *  The coin names what it toggles; the chip's inverted fill says whether it is on. */
  compact?: boolean
}

export const CockpitShowQuota = ({ compact = false }: ICockpitShowQuotaProps) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const showQuota = useSelector(selectCockpitShowQuota)

  const handleChange = useCallback(
    (value: boolean) => dispatch(setCockpitShowQuota(value)),
    [dispatch]
  )

  if (compact) {
    /* The button says what a click does: with the quota on screen it offers to hide it. */
    const actionKey = showQuota ? KEYS.hideLabel : KEYS.label

    return (
      <button
        type="button"
        className={cn(styles.chip, { [styles.on]: showQuota })}
        aria-pressed={showQuota}
        aria-label={t(actionKey)}
        onClick={() => handleChange(!showQuota)}
      >
        <span className={styles.caption}>
          <Translation text={actionKey} span />
        </span>
        <MoneyIcon />
      </button>
    )
  }

  return (
    <div className={styles.root}>
      <Switch
        label={<Translation text={KEYS.label} span />}
        checked={showQuota}
        onChange={handleChange}
      />
    </div>
  )
}

CockpitShowQuota.displayName = 'CockpitShowQuota'
